import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Loader2, Trash2, Save } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { useFb } from "../../utils/football";
import { toast } from "sonner";

const EMPTY = { sponsor_name: "", logo_url: "", brand_color: "#0b3d2e", banner_text: "", click_url: "", starts_at: "", ends_at: "" };
const toLocalInput = (iso) => { if (!iso) return ""; try { return new Date(iso).toISOString().slice(0, 16); } catch { return ""; } };
const toIso = (local) => { if (!local) return null; try { return new Date(local).toISOString(); } catch { return null; } };

export default function AdminMiniCupSponsorPage() {
  const fb = useFb();
  const { dbUser } = useAuth();
  const [form, setForm] = useState(EMPTY);
  const [sponsors, setSponsors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const d = await fb.get("/admin/football/mini-cup/sponsor"); setSponsors(d.sponsors || []); } catch {}
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (dbUser && !dbUser.is_admin) return <Navigate to="/" replace />;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.sponsor_name.trim()) { toast.error("Sponsor name required"); return; }
    setSaving(true);
    try {
      await fb.post("/admin/football/mini-cup/sponsor", {
        ...form, starts_at: toIso(form.starts_at), ends_at: toIso(form.ends_at),
      });
      toast.success("Sponsor saved");
      setForm(EMPTY); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  const edit = (s) => setForm({ ...EMPTY, ...s, starts_at: toLocalInput(s.starts_at), ends_at: toLocalInput(s.ends_at) });
  const del = async (id) => { if (!window.confirm("Delete sponsor?")) return; try { await fb.del(`/admin/football/mini-cup/sponsor/${id}`); toast.success("Deleted"); load(); } catch {} };

  return (
    <div className="football-theme mx-auto max-w-2xl pb-10" data-testid="admin-minicup-sponsor">
      <h1 className="mb-4 text-2xl font-extrabold">Penalty Cup — Sponsor</h1>

      <div className="space-y-3 rounded-xl border border-border bg-card p-4">
        {[["sponsor_name", "Sponsor name", "text"], ["logo_url", "Logo URL", "text"], ["banner_text", "Banner text override", "text"], ["click_url", "Click destination URL", "text"]].map(([k, label, type]) => (
          <div key={k}>
            <label className="mb-1 block text-xs font-bold text-muted-foreground">{label}</label>
            <input type={type} value={form[k]} onChange={(e) => set(k, e.target.value)}
              className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid={`sponsor-${k}`} />
          </div>
        ))}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="mb-1 block text-xs font-bold text-muted-foreground">Brand color</label>
            <input type="color" value={form.brand_color} onChange={(e) => set("brand_color", e.target.value)} className="h-10 w-full rounded-lg border border-border bg-background" data-testid="sponsor-brand_color" />
          </div>
          <div />
          <div>
            <label className="mb-1 block text-xs font-bold text-muted-foreground">Active from</label>
            <input type="datetime-local" value={form.starts_at} onChange={(e) => set("starts_at", e.target.value)} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="sponsor-starts_at" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-bold text-muted-foreground">Active until</label>
            <input type="datetime-local" value={form.ends_at} onChange={(e) => set("ends_at", e.target.value)} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="sponsor-ends_at" />
          </div>
        </div>
        <button onClick={save} disabled={saving} className="flex w-full items-center justify-center gap-2 rounded-lg wc-bg-gold py-2.5 font-extrabold text-slate-900 disabled:opacity-50" data-testid="sponsor-save-btn">
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save Sponsor
        </button>
      </div>

      <h2 className="mb-2 mt-6 text-lg font-bold">Configured sponsors</h2>
      {loading ? <Loader2 className="animate-spin" /> : sponsors.length === 0 ? (
        <p className="text-sm text-muted-foreground">No sponsors yet.</p>
      ) : (
        <div className="space-y-2">
          {sponsors.map((s) => (
            <div key={s.id} className="flex items-center gap-3 rounded-xl border border-border bg-card p-3" data-testid={`sponsor-item-${s.id}`}>
              <span className="h-6 w-6 rounded" style={{ background: s.brand_color }} />
              <div className="flex-1">
                <div className="text-sm font-bold">{s.sponsor_name}</div>
                <div className="text-[11px] text-muted-foreground">{(s.starts_at || "?").slice(0, 10)} → {(s.ends_at || "?").slice(0, 10)} · {s.total_clicks || 0} clicks</div>
                {s.click_stats && Object.keys(s.click_stats).length > 0 && (
                  <div className="text-[10px] text-muted-foreground">{Object.entries(s.click_stats).map(([k, v]) => `${k}:${v}`).join(" · ")}</div>
                )}
              </div>
              <button onClick={() => edit(s)} className="text-xs font-bold wc-gold" data-testid={`sponsor-edit-${s.id}`}>Edit</button>
              <button onClick={() => del(s.id)} className="text-red-400" data-testid={`sponsor-delete-${s.id}`}><Trash2 size={16} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
